import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  Paper,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  VideoLibrary as VideoLibraryIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { API } from '../config/apiConfig';

function ChannelEdit() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [channelData, setChannelData] = useState(null);
  const [formData, setFormData] = useState({
    channelName: '',
    channelDescription: '',
  });

  useEffect(() => {
    fetchChannelData();
  }, [channelId]);

  const fetchChannelData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await axios.get(API.channelById(channelId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.channel) {
        const channel = response.data.channel;
        setChannelData(channel);
        setFormData({
          channelName: channel.ChannelName || '',
          channelDescription: channel.ChannelDescription || '',
        });
      }
    } catch (err) {
      console.error('Error fetching channel data:', err);
      setError('Failed to load channel data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.channelName.trim()) {
      alert('Channel name is required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const response = await axios.put(
        API.updateChannel(channelId),
        {
          channelName: formData.channelName,
          channelDescription: formData.channelDescription,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        alert('Channel updated successfully!');
        navigate(`/dashboard/channels/${channelId}`);
      }
    } catch (err) {
      console.error('Error updating channel:', err);
      alert(err.response?.data?.error || 'Failed to update channel');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');

      const response = await axios.delete(API.deleteChannel(channelId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        alert('Channel deleted successfully');
        navigate('/dashboard/channels');
      }
    } catch (err) {
      console.error('Error deleting channel:', err);
      alert(err.response?.data?.error || 'Failed to delete channel');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/dashboard/channels/${channelId}`)}
        sx={{ mb: 2 }}
      >
        Back to Channel
      </Button>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Edit Channel
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Channel Info Card */}
        {channelData && (
          <Card
            sx={{
              mb: 3,
              background: 'linear-gradient(135deg, #667eea22 0%, #764ba222 100%)',
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Channel Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VideoLibraryIcon fontSize="small" color="primary" />
                  <Typography variant="body2">
                    Videos: {channelData.ActiveVideoCount || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VisibilityIcon fontSize="small" color="primary" />
                  <Typography variant="body2">
                    Views: {channelData.TotalViews || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            required
            label="Channel Name"
            value={formData.channelName}
            onChange={handleChange('channelName')}
            disabled={saving}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.channelDescription}
            onChange={handleChange('channelDescription')}
            disabled={saving}
            multiline
            rows={4}
            placeholder="Tell viewers about your channel"
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
            fullWidth
            sx={{ mb: 2 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>

          <Button
            variant="outlined"
            color="error"
            size="large"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            fullWidth
          >
            Delete Channel
          </Button>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Channel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this channel?
            <br />
            <strong>This will also deactivate all videos in this channel.</strong>
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ChannelEdit;

